/**
 * Guardian Status Utilities - Laravel Compatible
 *
 * This utility provides Laravel-compatible status checking methods
 * for guardian status transitions and business logic.
 *
 * Matches Laravel's Guardian model methods exactly.
 * 
 * Also handles old status IDs (14-19) by normalizing them to Laravel IDs (1-13).
 */

// Status ID mapping: old IDs (14-19) → Laravel IDs (1-13)
const STATUS_ID_MAPPING = {
  14: 12, // Vetted (old) → Vetted (Laravel)
  15: 2,  // Documents Required (old) → Documents Required (Laravel)
  16: 6,  // Active (old) → Placed (Laravel)
  17: 13, // Inactive (old) → Archived (Laravel)
  18: 8,  // Blacklisted (old) → Blacklisted (Laravel)
  19: 7,  // Finished (old) → Previous Guardian (Laravel)
};

/**
 * Normalize status ID from old format (14-19) to Laravel format (1-13)
 * @param {number} statusId - Status ID to normalize
 * @returns {number} Normalized status ID (1-13)
 */
function normalizeStatusId(statusId) {
  if (!statusId) return null;
  
  // If status ID is in old format (14-19), map it to Laravel format
  if (STATUS_ID_MAPPING[statusId]) {
    return STATUS_ID_MAPPING[statusId];
  }
  
  // If status ID is already in Laravel format (1-13), return as is
  return statusId;
}

// Import guardian configuration
const guardianConfig = {
  status: {
    registered: { id: 1, name: "Registered", color: "#000" },
    documents_required: { id: 2, name: "Documents Required", color: "purple" },
    property_assigned: { id: 3, name: "Assigned", color: "green" },
    payment_pending: { id: 4, name: "Payment Pending", color: "orange" },
    awaiting_key_collection: {
      id: 5,
      name: "Awaiting Key Collection",
      color: "blue",
    },
    placed: { id: 6, name: "Placed", color: "green" },
    previous_guardian: { id: 7, name: "Previous Guardian", color: "gray" },
    blacklisted: { id: 8, name: "Blacklisted", color: "red" },
    not_interested: { id: 9, name: "Not Interested", color: "gray" },
    ntq: { id: 10, name: "NTQ", color: "red" },
    documents_uploaded: { id: 11, name: "Documents Uploaded", color: "blue" },
    vetted: { id: 12, name: "Vetted", color: "#573b87" },
    archived: { id: 13, name: "Archived", color: "gray" },
  },
};

/**
 * Check if guardian is in Registered status (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian is registered
 */
export const isRegistered = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.registered.id;
};

/**
 * Check if guardian is in Vetted status (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian is vetted
 */
export const isInVetted = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.vetted.id;
};

/**
 * Check if guardian is in Documents Required status (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian needs documents
 */
export const isInDocumentsRequired = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.documents_required.id;
};

/**
 * Check if guardian is in Documents Uploaded status (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian has uploaded documents
 */
export const isInDocumentsUploaded = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.documents_uploaded.id;
};

/**
 * Check if guardian is Assigned to property (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian is assigned
 */
export const isAssigned = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.property_assigned.id;
};

/**
 * Check if guardian is in Payment Pending status (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian has pending payment
 */
export const isInPaymentPending = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.payment_pending.id;
};

/**
 * Check if guardian is Awaiting Key Collection (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian is awaiting keys
 */
export const isAwaitingKeyCollection = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.awaiting_key_collection.id;
};

/**
 * Check if guardian is Placed (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian is placed
 */
export const isPlaced = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.placed.id;
};

/**
 * Check if guardian is NTQ (Notice to Quit) (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian has NTQ
 */
export const isNTQ = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.ntq.id;
};

/**
 * Check if guardian is Previous Guardian (Laravel method)
 * @param {number} statusId - Guardian status ID (normalized automatically)
 * @returns {boolean} Whether guardian is previous guardian
 */
export const isPreviousGuardian = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  return normalizedId === guardianConfig.status.previous_guardian.id;
};

/**
 * Check if guardian can be assigned to room (Laravel method)
 * Laravel CURRENT logic: return $this->isInVetted();
 * Laravel COMMENTED old logic: return $this->isRegistered() || $this->isInDocumentsRequired() || $this->isPreviousGuardian();
 * @param {number} statusId - Guardian status ID
 * @returns {boolean} Whether guardian can be assigned
 */
export const canBeAssigned = (statusId) => {
  // Laravel's ACTIVE implementation: only Vetted status (12)
  return isInVetted(statusId);
};

/**
 * Check if guardian can be moved to another room (Laravel method)
 * @param {number} statusId - Guardian status ID
 * @returns {boolean} Whether guardian can be moved
 */
export const canBeMoved = (statusId) => {
  return (
    isAwaitingKeyCollection(statusId) || isPlaced(statusId) || isNTQ(statusId)
  );
};

/**
 * Check if guardian can have changed payments on rent (Laravel method)
 * @param {number} statusId - Guardian status ID
 * @returns {boolean} Whether guardian can have changed payments
 */
export const canHaveChangedPaymentsOnRent = (statusId) => {
  return (
    isInPaymentPending(statusId) ||
    isAwaitingKeyCollection(statusId) ||
    isPlaced(statusId) ||
    isInVetted(statusId)
  );
};

/**
 * Check if guardian can see available spaces (Laravel method)
 * @param {number} statusId - Guardian status ID
 * @param {Object} guardian - Guardian object with latest rent info
 * @returns {boolean} Whether guardian can see available spaces
 */
export const canSeeAvailableSpaces = (statusId, guardian = null) => {
  // Laravel logic from canSeeAvailableSpaces method
  if (
    isRegistered(statusId) ||
    isInVetted(statusId) ||
    isPlaced(statusId) ||
    isNTQ(statusId) ||
    isPreviousGuardian(statusId)
  ) {
    return true;
  }

  // Check documents required/uploaded without rent
  if (
    (isInDocumentsRequired(statusId) || isInDocumentsUploaded(statusId)) &&
    (!guardian || !guardian.latest_not_ended_or_failed_rent)
  ) {
    return true;
  }

  return false;
};

/**
 * Get all status transition methods for a guardian
 * @param {number} statusId - Guardian status ID
 * @returns {Object} Object with all status check results
 */
export const getStatusChecks = (statusId) => {
  return {
    isRegistered: isRegistered(statusId),
    isInVetted: isInVetted(statusId),
    isInDocumentsRequired: isInDocumentsRequired(statusId),
    isInDocumentsUploaded: isInDocumentsUploaded(statusId),
    isAssigned: isAssigned(statusId),
    isInPaymentPending: isInPaymentPending(statusId),
    isAwaitingKeyCollection: isAwaitingKeyCollection(statusId),
    isPlaced: isPlaced(statusId),
    isNTQ: isNTQ(statusId),
    isPreviousGuardian: isPreviousGuardian(statusId),
    canBeAssigned: canBeAssigned(statusId),
    canBeMoved: canBeMoved(statusId),
    canHaveChangedPaymentsOnRent: canHaveChangedPaymentsOnRent(statusId),
  };
};

/**
 * Get status name by ID
 * @param {number} statusId - Status ID (normalized automatically)
 * @returns {string} Status name
 */
export const getStatusName = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  const status = Object.values(guardianConfig.status).find(
    (s) => s.id === normalizedId
  );
  return status ? status.name : `Unknown Status (${statusId})`;
};

/**
 * Get status color by ID
 * @param {number} statusId - Status ID (normalized automatically)
 * @returns {string} Status color
 */
export const getStatusColor = (statusId) => {
  const normalizedId = normalizeStatusId(statusId);
  const status = Object.values(guardianConfig.status).find(
    (s) => s.id === normalizedId
  );
  return status ? status.color : "#666";
};

/**
 * Get all available statuses
 * @returns {Array} Array of status objects
 */
export const getAllStatuses = () => {
  return Object.values(guardianConfig.status);
};

// Default export with all methods
export default {
  isRegistered,
  isInVetted,
  isInDocumentsRequired,
  isInDocumentsUploaded,
  isAssigned,
  isInPaymentPending,
  isAwaitingKeyCollection,
  isPlaced,
  isNTQ,
  isPreviousGuardian,
  canBeAssigned,
  canBeMoved,
  canHaveChangedPaymentsOnRent,
  canSeeAvailableSpaces,
  getStatusChecks,
  getStatusName,
  getStatusColor,
  getAllStatuses,
  normalizeStatusId, // Export normalization function
  config: guardianConfig,
};


